import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useToast } from "@/components/ui/use-toast";
import { 
  CheckCircle, 
  XCircle, 
  Clock, 
  Mail, 
  Shield, 
  Loader2
} from 'lucide-react';

interface InvitationDetails {
  id: string;
  email: string;
  group: {
    id: number;
    name: string;
    description: string;
    level: number;
    permissions: string[];
  };
  inviter: {
    name: string;
    email: string;
    organization: string;
    avatar_url?: string;
  };
  message?: string;
  status: 'pending' | 'accepted' | 'declined' | 'expired';
  created_at: string;
  expires_at: string;
}

interface InvitationDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  inviteId: string;
  token: string;
  onInvitationAccepted?: () => void;
  onInvitationDeclined?: () => void;
}

export default function InvitationDetailsModal({
  isOpen,
  onClose,
  inviteId,
  token,
  onInvitationAccepted,
  onInvitationDeclined
}: InvitationDetailsModalProps) {
  const [invitation, setInvitation] = useState<InvitationDetails | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const API_BASE_URL = import.meta.env.VITE_CC_API_URL;

  useEffect(() => {
    if (isOpen && inviteId && token) {
      fetchInvitationDetails();
    }
  }, [isOpen, inviteId, token]);

  const fetchInvitationDetails = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/v1/me/invitations/${inviteId}?token=${token}`);
      
      if (response.ok) {
        const data = await response.json();
        setInvitation(data.data);
      } else if (response.status === 404) {
        setError('Invitation not found or has expired');
      } else if (response.status === 410) {
        setError('This invitation has already been used or expired');
      } else {
        setError('Failed to load invitation details');
      }
    } catch (error) {
      setError('Failed to load invitation details');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAcceptInvitation = async () => {
    if (!invitation || !token) return;

    setIsProcessing(true);
    try {
      const response = await fetch(`${API_BASE_URL}/v1/me/invitations/${invitation.id}/accept`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token,
          redirect_url: `${window.location.origin}/dashboard`
        })
      });

      if (response.ok) {
        const data = await response.json();
        
        toast({
          title: "Invitation accepted!",
          description: "You have successfully joined the organization.",
        });

        setInvitation(prev => prev ? { ...prev, status: 'accepted' } : null);
        onInvitationAccepted?.();

        // If user needs to sign up or sign in, redirect to auth flow
        if (data.requires_auth) {
          window.location.href = `${API_BASE_URL}/auth/signup?invite_token=${token}&redirect_url=${encodeURIComponent(window.location.origin + '/dashboard')}`;
        } else {
          // Close modal after a brief delay to show success state
          setTimeout(() => {
            onClose();
          }, 1500);
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to accept invitation');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to accept invitation",
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeclineInvitation = async () => {
    if (!invitation || !token) return;

    setIsProcessing(true);
    try {
      const response = await fetch(`${API_BASE_URL}/v1/me/invitations/${invitation.id}/decline`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token })
      });

      if (response.ok) {
        toast({
          title: "Invitation declined",
          description: "You have declined the invitation.",
        });
        setInvitation(prev => prev ? { ...prev, status: 'declined' } : null);
        onInvitationDeclined?.();
        
        // Close modal after a brief delay to show success state
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        throw new Error('Failed to decline invitation');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to decline invitation",
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    if (!isProcessing) {
      setInvitation(null);
      setError(null);
      onClose();
    }
  };

  if (!isOpen) return null;

  const isExpired = invitation ? new Date(invitation.expires_at) < new Date() : false;
  const isAccepted = invitation?.status === 'accepted';
  const isDeclined = invitation?.status === 'declined';

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-center">
            {isAccepted ? 'Invitation Accepted' :
             isDeclined ? 'Invitation Declined' :
             isExpired ? 'Invitation Expired' :
             'Organization Invitation'}
          </DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="text-center">
              <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
              <p className="text-gray-600">Loading invitation details...</p>
            </div>
          </div>
        ) : error ? (
          <div className="text-center py-8">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Error Loading Invitation</h3>
            <p className="text-gray-600 mb-4">{error}</p>
            <Button onClick={handleClose} variant="outline">
              Close
            </Button>
          </div>
        ) : !invitation ? (
          <div className="text-center py-8">
            <Mail className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Invitation Not Found</h3>
            <p className="text-gray-600">The invitation details could not be loaded.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Status Icon */}
            <div className="flex justify-center">
              {isAccepted ? (
                <CheckCircle className="w-16 h-16 text-green-500" />
              ) : isDeclined ? (
                <XCircle className="w-16 h-16 text-red-500" />
              ) : isExpired ? (
                <Clock className="w-16 h-16 text-gray-400" />
              ) : (
                <Mail className="w-16 h-16 text-blue-500" />
              )}
            </div>

            {/* Invitation Status */}
            <div className="flex justify-center">
              <Badge 
                variant={isAccepted ? "default" : isDeclined ? "destructive" : isExpired ? "secondary" : "outline"}
                className="px-3 py-1"
              >
                {isAccepted ? 'Accepted' :
                 isDeclined ? 'Declined' :
                 isExpired ? 'Expired' :
                 'Pending'}
              </Badge>
            </div>

            {/* Inviter Information */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-medium mb-3">Invited by</h3>
              <div className="flex items-center space-x-3">
                <Avatar>
                  <AvatarImage src={invitation.inviter.avatar_url} />
                  <AvatarFallback>
                    {invitation.inviter.name.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-medium">{invitation.inviter.name}</p>
                  <p className="text-sm text-gray-600">{invitation.inviter.email}</p>
                  <p className="text-sm text-gray-600">{invitation.inviter.organization}</p>
                </div>
              </div>
            </div>

            {/* Role Information */}
            <div className="bg-blue-50 rounded-lg p-4">
              <h3 className="font-medium mb-3 flex items-center">
                <Shield className="w-4 h-4 mr-2" />
                Role Details
              </h3>
              <div className="space-y-2">
                <div>
                  <p className="font-medium">{invitation.group.name}</p>
                  <p className="text-sm text-gray-600">{invitation.group.description}</p>
                  <p className="text-sm text-gray-600">Permission Level: {invitation.group.level}</p>
                </div>
                <div>
                  <p className="text-sm font-medium mb-1">Permissions:</p>
                  <div className="flex flex-wrap gap-1">
                    {invitation.group.permissions.map((permission) => (
                      <Badge key={permission} variant="outline" className="text-xs">
                        {permission}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Personal Message */}
            {invitation.message && (
              <div className="bg-yellow-50 rounded-lg p-4">
                <h3 className="font-medium mb-2">Personal Message</h3>
                <p className="text-gray-700 italic">"{invitation.message}"</p>
              </div>
            )}

            {/* Invitation Details */}
            <div className="text-sm text-gray-600 space-y-1">
              <p>Invitation sent: {new Date(invitation.created_at).toLocaleDateString()}</p>
              <p>Expires: {new Date(invitation.expires_at).toLocaleDateString()}</p>
              <p>Invited email: {invitation.email}</p>
            </div>

            {/* Action Buttons */}
            {!isAccepted && !isDeclined && !isExpired && (
              <div className="flex space-x-3">
                <Button
                  onClick={handleAcceptInvitation}
                  disabled={isProcessing}
                  className="flex-1"
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Accept Invitation
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  onClick={handleDeclineInvitation}
                  disabled={isProcessing}
                  className="flex-1"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Decline
                </Button>
              </div>
            )}

            {(isAccepted || isDeclined) && (
              <div className="text-center">
                <Button onClick={handleClose} variant="outline">
                  Close
                </Button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}